// class used mainly to map json to types
import dayjs from 'dayjs';
import moment from 'moment';
import { MatTableDataSource } from '@angular/material/table';
import { TYPES_I18N } from './types.i18n';

export interface RecordData {
    meta: {
        measurer: {
            userId: string;
            lastName: string;
            firstName: string;
        };
        user: {
            userId: string;
            lastName: string;
            firstName: string;
            code: string;
            displayName: string;
        };
        location: {
            sectionId: string;
            sectionName: string;
        };
    };
    data: any;
    commonInfo: {
        lastModifiedAtStr: string;
        lastModifiedAt: dayjs.Dayjs;
        lastModifiedBy: string;
    };
    id: string;
}

export interface GenericDataType {
    userType: string;
    age: number;
    sex: string;
    text: string;

    createdAt?: string;
    createdAtDayjs?: dayjs.Dayjs;
    createdAtLocalStr?: string;
    createdAtLocationStr?: string;
    differentLocation?: boolean;

    asOfDate? : string;

    testDuration?: number;
    totalTime?: string;

    recordType: string;
    recordTypeView: string;
}

export interface ScreeningDataType extends GenericDataType {
    storage: {
        audio: string;
        data: string;
    };

    nbModel?: string;
    nbId?: string;

    wavDuration?: string;
    totalMinutes?: number;

    numOfSwallowings: number;
    swallowingsTimeAverage: number;
    swallowingsTimeVariance: number;
    numOfCoughs: number;

    // evaluation?: number;

    // ------- added with nb4 -----//
    temperatureAvg?: number;
    temperatureMax?: number;
    hrAvg?: number;
    hrMin?: number;
    hrMax?: number;
    spo2Avg?: number;
    spo2Min?: number;
    spo2Max?: number;

    // MWST/FOOD_TEST: 試行詳細・最終スコア（recordType が MWST/FOOD_TEST の場合に存在）
    attemptsDetail?: AttemptDetail[];
    finalScore?: number;
}

export interface MonitoringDataType extends GenericDataType {
    storage: {
        audio: string;
        data: string;
        beforeMealFoodImage?: string;
        afterMealFoodImage?: string;
    };

    nbModel?: string;
    nbId?: string;

    wavDuration?: string;
    totalMinutes?: number;

    numOfSwallowings: number;
    swallowingsTimeAverage: number;
    swallowingsTimeVariance: number;
    numOfCoughs: number;

    beforeMealNeckAngle?: number;
    beforeMealBodyAngle?: number;

    // ------- with vitals -----//
    temperatureAvg?: number;
    temperatureMax?: number;
    hrAvg?: number;
    hrMin?: number;
    hrMax?: number;
    spo2Avg?: number;
    spo2Min?: number;
    spo2Max?: number;

    beforeMealHrFingerAvg?: number;
    beforeMealHrFingerMin?: number;
    beforeMealHrFingerMax?: number;
    beforeMealSpo2FingerAvg?: number;
    beforeMealSpo2FingerMin?: number;
    beforeMealSpo2FingerMax?: number;
    afterMealHrFingerAvg?: number;
    afterMealHrFingerMin?: number;
    afterMealHrFingerMax?: number;
    afterMealSpo2FingerAvg?: number;
    afterMealSpo2FingerMin?: number;
    afterMealSpo2FingerMax?: number;
}

export interface VitalDataType extends GenericDataType {
    storage: {
        audio: string;
        data: string;
    };

    nbModel?: string;
    nbId?: string;

    wavDuration?: string;
    totalMinutes?: number;

    temperatureAvg?: number;
    temperatureMax?: number;
    hrAvg?: number;
    hrMin?: number;
    hrMax?: number;
    spo2Avg?: number;
    spo2Min?: number;
    spo2Max?: number;
    spo2FingerAvg?: string;
    spo2FingerMin?: number;
    spo2FingerMax?: number;
}

export interface GostDataType extends GenericDataType {
    // ----- gost data ------- //
    termsAgreed: boolean;
    questionnaire1Answers: QuestionnaireAnswer[];
    questionnaire2Answers: QuestionnaireAnswer[];
    newsScore?: {

    }
    stepwiseWST?: {

    }[];
    challenge?: {

    };
    gostPath?: string[];
    gostScore?: number;
    mGostScore?: number;

    //----- if has file recording ----//
    storage?: {
        audio: string;
        data: string;
    };

    nbModel?: string;
    nbId?: string;
    wavDuration?: string;
    totalMinutes?: number;
    // gostRecordCreatedAt?: string;
    gostRecordId?: string;
    // recordTestDuration?: string;
    // recordTotalTime?: string;
}

export interface SstpDataType extends GenericDataType {
    // ----- sst+ data ------- //
    stepwiseWST?: {    }[];
    challenge?: {    };
    sstpPath?: { };
    gostScore?: number;
    mGostScore?: number;

    //----- if has file recording ----//
    storage?: {
        audio: string;
        data: string;
    };

    nbModel?: string;
    nbId?: string;
    wavDuration?: string;
    totalMinutes?: number;
    sstpRecordId?: string;
}

export interface MwstFtGDataType extends GenericDataType {
    // ----- mwst or food test data ------- //
    attemptsDetail: AttemptDetail[];
    finalScore: number;

    //----- if has file recording ----//
    storage?: {
        audio: string;
        data: string;
    };

    nbModel?: string;
    nbId?: string;
    wavDuration?: string;
    totalMinutes?: number;
    mwstFTGRecordId?: string;
}

export interface VsDataType extends GenericDataType {

    //----- if has file recording ----//
    storage?: {
        audio: string;
        data: string;

        streamingURL : string;
        streamingToken : string;
        mediaStatus : string;
        mediaType : string;
    };

    nbModel?: string;
    nbId?: string;
    wavDuration?: string;
    totalMinutes?: number;
    numOfSwallowings?: number;
    swallowingsTimeAverage?: number;
    swallowingsTimeVariance?: number;
    numOfCoughs?: number;
    mediaRecordId?: string;
}

export interface TomassDataType extends GenericDataType {

    //----- if has file recording ----//
    storage?: {
        audio: string;
        data: string;
    };

    nbModel?: string;
    nbId?: string;
    wavDuration?: string;
    totalMinutes?: number;

    TomassRecordId?: string;

    ageGroup? : string;
    geolocation? : string;
    pst? : number;
    gpst? : number;
    score? : string;

}

export abstract class TypesUtils {
    public static convertJsonToRecords(responseData: any, generateMonitoringcalendarData: boolean)
        : { records: RecordData[]; monitoringDaysData: MonitoringDayData[] } {
        var records = [];
        var allMonitoringDayData: MonitoringDayData[] = [];

        var previousMonitoringMoment: moment.Moment = null;

        for (let i = 0; i < responseData.length; i++) {
            // RecordsList では バックエンドの RecordDocument がそのまま入っているが、対応する recordData 相当のデータ項目は入っていない
            // RecordGet では RecordDocument の主要フィールドはほとんどそのまま入っている以外に recordData 相当のデータの一部が data 項目の中に追加で入っているが、これを使わないようにしたい。2023/6/21
            // 一方で RecordWithDataGet　では record, recordData それぞれの項目の中に RecordDocument 及び recordData 相当のデータが入っている。
            // "record"項目が無いときは responseData[i] そのものが RecordDocument 相当、あるときは "record" 項目が RecordDocument相当であると読み替えることで、どちらの形式のデータが来ても変換できる
            // また、 "record"項目があるときは"recordData"項目も存在するはずで、旧 RecordGet の追加項目はこちらから取得できる
            var record = responseData[i];
            var recordData = null;
            if (responseData[i].record != null){
                record = responseData[i].record;
                recordData = responseData[i].recordData;
            }

            //var createdAt = responseData[i].data.createdAt;
            var createdAt = record.data.createdAt;
            var createdAtDayjs = dayjs(createdAt);
            var createdAtLocalStr = (createdAtDayjs.isValid()) ? createdAtDayjs.format('YYYY/MM/DD HH:mm:ss') : 'ERROR';

            var asOfDate = record.data.asOfDate;

            var offsetSign = createdAt.charAt(createdAt.length - 6); // to get the offset of the dateTime (when the record was actually created)

            // var createdAtLocationDayjs = null;
            var createdAtLocationStr = '';
            var differentLocation = false;

            if (offsetSign === '-' || offsetSign === '+') {
                var offsetArr = (createdAt.substr(createdAt.length - 5)).split(':');
                var offsetSign = createdAt.charAt(createdAt.length - 6);

                var offset: number = parseInt(offsetArr[0]) * 60 + parseInt(offsetArr[1]);

                var createdAtLocationDayjs = createdAtDayjs.utcOffset(offset, false);
                if(dayjs.isDayjs(createdAtLocationDayjs)){
                    createdAtLocationStr = createdAtLocationDayjs.format('YYYY/MM/DD HH:mm:ssZ');
                    differentLocation = createdAtDayjs.format('YYYY/MM/DD HH:mm:ss') !== createdAtLocationDayjs.format('YYYY/MM/DD HH:mm:ss');
                }
            }

            //var lastModifiedAt = responseData[i].commonInfo.lastModifiedAt;
            var lastModifiedAt = record.commonInfo.lastModifiedAt;
            var lastModifiedAtDayjs = dayjs(lastModifiedAt);
            var lastModifiedAtStr = (lastModifiedAtDayjs.isValid()) ? lastModifiedAtDayjs.format('YYYY/MM/DD HH:mm:ss') : 'ERROR';

            var recordTypeView = '';
            //var recordType = responseData[i].data.recordType;
            var recordType = record.data.recordType;
            var version = record.data.version;

            recordTypeView = getRecordTypeDisplay(recordType);
            // switch (recordType) {
            //     case 'RSST':
            //         recordTypeView = 'RSST';
            //         break;
            //     case 'MWST':
            //     case 'MWST_G':
            //         recordTypeView = 'MWST';
            //         break;
            //     case 'FOOD_TEST':
            //     case 'FOOD_TEST_G':
            //         recordTypeView = 'Food test';
            //         break;
            //     case 'MONITORING':
            //     case 'MEAL_MONITORING':
            //     case 'MEAL_MONITORING_WITH_VITALS':
            //         recordTypeView = 'Monitoring';
            //         break;
            //     case 'VITAL':
            //         recordTypeView = 'Vitals';
            //         break;
            //     case 'GOST':
            //     case 'GOST_DATA':
            //         recordTypeView = 'GOST';
            //         break;
            //     case 'VS':
            //     case 'VS_DATA':
            //         recordTypeView = 'VS';
            //         break;

            //     case 'TOMASS':
            //     case 'TOMASS_DATA':
            //         recordTypeView = 'TOMASS';
            //         break;

            //     case 'SSTP':
            //     case 'SSTP_DATA':
            //         recordTypeView = 'SSTP';
            //         break;

            //     default:
            //         break;
            // }

            var totalTime = record.data.totalTime;
            var wavDuration = record.data.wavDuration;
            var totalMinutes = wavDuration ? wavDuration / (60 * 1000) : 0;

            var numOfSwallowings = record.data.numOfSwallowings;
            var numOfCoughs = record.data.numOfCoughs;

            var data: GenericDataType = null;

            switch (recordType) {
                case 'RSST':
                case 'MWST':
                case 'FOOD_TEST':
                    (data as ScreeningDataType) = {
                        userType: record.data.userType,
                        age: record.data.age,
                        sex: record.data.sex,
                        text: record.data.text,

                        testDuration: record.data.testDuration,
                        totalTime: totalTime,

                        recordType: recordType,
                        recordTypeView: recordTypeView,

                        storage: {
                            ...record.data.storage,
                        },
                        nbModel: record.data.nbModel,
                        nbId: record.data.nbId,
                        wavDuration: wavDuration,
                        totalMinutes: totalMinutes,
                        numOfSwallowings: numOfSwallowings,
                        swallowingsTimeAverage: record.data.swallowingsTimeAverage,
                        swallowingsTimeVariance: record.data.swallowingsTimeVariance,
                        numOfCoughs: numOfCoughs,
                        temperatureAvg: record.data.temperatureAvg,
                        temperatureMax: record.data.temperatureMax,
                        hrAvg: record.data.hrAvg,
                        hrMin: record.data.hrMin,
                        hrMax: record.data.hrMax,
                        spo2Avg: record.data.spo2Avg,
                        spo2Min: record.data.spo2Min,
                        spo2Max: record.data.spo2Max,
                        attemptsDetail: recordData?.data.attemptsDetail,
                        finalScore: recordData?.data.finalScore,
                    };
                    break;

                case 'MONITORING':
                case 'MEAL_MONITORING':
                case 'MEAL_MONITORING_WITH_VITALS':
                    (data as MonitoringDataType) = {
                        userType: record.data.userType,
                        age: record.data.age,
                        sex: record.data.sex,
                        text: record.data.text,

                        testDuration: record.data.testDuration,
                        totalTime: totalTime,

                        recordType: recordType,
                        recordTypeView: recordTypeView,

                        storage: {
                            ...record.data.storage,
                        },
                        nbModel: record.data.nbModel,
                        nbId: record.data.nbId,
                        wavDuration: wavDuration,
                        totalMinutes: totalMinutes,
                        numOfSwallowings: numOfSwallowings,
                        swallowingsTimeAverage: record.data.swallowingsTimeAverage,
                        swallowingsTimeVariance: record.data.swallowingsTimeVariance,
                        numOfCoughs: numOfCoughs,
                        beforeMealNeckAngle: record.data.beforeMealNeckAngle,
                        beforeMealBodyAngle: record.data.beforeMealBodyAngle,
                        temperatureAvg: record.data.temperatureAvg,
                        temperatureMax: record.data.temperatureMax,
                        hrAvg: record.data.hrAvg,
                        hrMin: record.data.hrMin,
                        hrMax: record.data.hrMax,
                        spo2Avg: record.data.spo2Avg,
                        spo2Min: record.data.spo2Min,
                        spo2Max: record.data.spo2Max,
                        beforeMealHrFingerAvg: record.data.beforeMealHrFingerAvg,
                        beforeMealHrFingerMin: record.data.beforeMealHrFingerMin,
                        beforeMealHrFingerMax: record.data.beforeMealHrFingerMax,
                        beforeMealSpo2FingerAvg: record.data.beforeMealSpo2FingerAvg,
                        beforeMealSpo2FingerMin: record.data.beforeMealSpo2FingerMin,
                        beforeMealSpo2FingerMax: record.data.beforeMealSpo2FingerMax,
                        afterMealHrFingerAvg: record.data.afterMealHrFingerAvg,
                        afterMealHrFingerMin: record.data.afterMealHrFingerMin,
                        afterMealHrFingerMax: record.data.afterMealHrFingerMax,
                        afterMealSpo2FingerAvg: record.data.afterMealSpo2FingerAvg,
                        afterMealSpo2FingerMin: record.data.afterMealSpo2FingerMin,
                        afterMealSpo2FingerMax: record.data.afterMealSpo2FingerMax,
                    };
                    break;

                case 'VITAL':
                    (data as VitalDataType) = {
                        userType: record.data.userType,
                        age: record.data.age,
                        sex: record.data.sex,
                        text: record.data.text,

                        testDuration: record.data.testDuration,
                        totalTime: totalTime,

                        recordType: recordType,
                        recordTypeView: recordTypeView,

                        storage: {
                            ...record.data.storage,
                        },
                        nbModel: record.data.nbModel,
                        nbId: record.data.nbId,
                        wavDuration: wavDuration,
                        totalMinutes: totalMinutes,
                        temperatureAvg: record.data.temperatureAvg,
                        temperatureMax: record.data.temperatureMax,
                        hrAvg: record.data.hrAvg,
                        hrMin: record.data.hrMin,
                        hrMax: record.data.hrMax,
                        spo2Avg: record.data.spo2Avg,
                        spo2Min: record.data.spo2Min,
                        spo2Max: record.data.spo2Max,
                        spo2FingerAvg: record.data.spo2FingerAvg,
                        spo2FingerMin: record.data.spo2FingerMin,
                        spo2FingerMax: record.data.spo2FingerMax,
                    };
                    break;

                case 'GOST':
                case 'GOST_DATA':
                case 'GOST_DATA_2':
                    switch(version){
                        case '2' :
                        case 2 :
                            (data as any) = { ...record.data, ...recordData?.data };
                            if ((data as any).storage == null && record.data?.storage != null) {
                                (data as any).storage = { ...record.data.storage };
                            }

                            data.recordType = recordType;
                            data.recordTypeView = recordTypeView;

                            break;
                        default :
                            (data as GostDataType) = {
                                userType: record.data.userType,
                                age: record.data.age,
                                sex: record.data.sex,
                                text: record.data.text,

                                createdAt: createdAt,
                                createdAtDayjs: createdAtDayjs,
                                createdAtLocalStr: createdAtLocalStr,
                                createdAtLocationStr: createdAtLocationStr,
                                differentLocation: differentLocation,

                                asOfDate : asOfDate,

                                testDuration: record.data.testDuration,
                                totalTime: totalTime,

                                recordType: recordType,
                                recordTypeView: recordTypeView,

                                termsAgreed: record.data.termsAgreed,
                                questionnaire1Answers: recordData?.data.questionnaire1Answers,
                                questionnaire2Answers: recordData?.data.questionnaire2Answers,
                                newsScore: recordData?.data.newsScore,
                                stepwiseWST: recordData?.data.stepwiseWST,
                                challenge: recordData?.data.challenge,
                                gostPath: recordData?.data.gostPath,
                                gostScore: recordData?.data.gostScore,
                                mGostScore: recordData?.data.mGostScore,
                                storage: {
                                    ...record.data.storage,
                                },
                                nbModel: record.data.nbModel,
                                nbId: record.data.nbId,
                                wavDuration: wavDuration,
                                totalMinutes: totalMinutes,
                                gostRecordId: recordData?.data.gostRecordId,
                            };
                            break;
                    }

                    break;

                case 'SSTP_DATA':
                case 'SSTP':
                    (data as SstpDataType) = {
                        userType: record.data.userType,
                        age: record.data.age,
                        sex: record.data.sex,
                        text: record.data.text,

                        testDuration: record.data.testDuration,
                        totalTime: totalTime,

                        recordType: recordType,
                        recordTypeView: recordTypeView,

                        stepwiseWST: recordData?.data.stepwiseWST,
                        challenge: recordData?.data.challenge,
                        sstpPath: recordData?.data.sstpPath,
                        gostScore: recordData?.data.gostScore,
                        mGostScore: recordData?.data.mGostScore,

                        storage: {
                            ...record.data.storage,
                        },

                        nbModel: record.data.nbModel,
                        nbId: record.data.nbId,
                        wavDuration: wavDuration,
                        totalMinutes: totalMinutes,
                        sstpRecordId: recordData?.data.sstpRecordId,
                    };
                    break;

                case 'MWST_G':
                case 'MWST_G_DATA':
                case 'FOOD_TEST_G':
                case 'FOOD_TEST_G_DATA':
                    (data as MwstFtGDataType) = {
                        userType: record.data.userType,
                        age: record.data.age,
                        sex: record.data.sex,
                        text: record.data.text,

                        testDuration: record.data.testDuration,
                        totalTime: totalTime,

                        recordType: recordType,
                        recordTypeView: recordTypeView,

                        attemptsDetail: recordData?.data.attemptsDetail,
                        finalScore: recordData?.data.finalScore,
                        storage: {
                            ...record.data.storage,
                        },
                        nbModel: record.data.nbModel,
                        nbId: record.data.nbId,
                        wavDuration: wavDuration,
                        totalMinutes: totalMinutes,
                        mwstFTGRecordId: recordData?.data.mwstFTGRecordId,
                    };
                    break;

                case 'VS':
                case "VS_DATA":
                    (data as VsDataType) = {
                        userType: record.data.userType,
                        age: record.data.age,
                        sex: record.data.sex,
                        text: record.data.text,

                        testDuration: record.data.testDuration,
                        totalTime: totalTime,

                        recordType: recordType,
                        recordTypeView: recordTypeView,

                        storage: {
                            ...record.data.storage,

                            streamingURL : recordData?.data?.storage?.streamingURL,
                            streamingToken : recordData?.data?.storage?.streamingToken,
                            mediaType : recordData?.data.mediaType,
                            mediaStatus : recordData?.data.mediaStatus,
                        },
                        nbModel: record.data.nbModel,
                        nbId: record.data.nbId,
                        wavDuration: wavDuration,
                        totalMinutes: totalMinutes,
                        numOfSwallowings: numOfSwallowings,
                        swallowingsTimeAverage: record.data.swallowingsTimeAverage,
                        swallowingsTimeVariance: record.data.swallowingsTimeVariance,
                        numOfCoughs: numOfCoughs,
                        mediaRecordId: recordData?.data.mediaRecordId,
                    };
                    break;

                case 'TOMASS':
                case "TOMASS_DATA":
                    (data as TomassDataType) = {
                        userType: record.data.userType,
                        age: record.data.age,
                        sex: recordData?.data.sex,
                        text: record.data.text,

                        testDuration: record.data.testDuration,
                        totalTime: totalTime,

                        recordType: recordType,
                        recordTypeView: recordTypeView,

                        storage: {
                            ...record.data.storage,
                        },

                        nbModel: record.data.nbModel,
                        nbId: record.data.nbId,
                        wavDuration: wavDuration,
                        totalMinutes: totalMinutes,

                        TomassRecordId: recordData?.data.tomassRecordId,

                        ageGroup : recordData?.data.ageGroup,
                        geolocation : recordData?.data.geolocation,
                        pst : recordData?.data.pst,
                        gpst : recordData?.data.gpst,
                        score : recordData?.data.score,

                    };
                    break;

                default:
                    data = null;
                    break;
            }

            if(data != null){

                data.createdAt = createdAt;
                data.createdAtDayjs = createdAtDayjs;
                data.createdAtLocalStr = createdAtLocalStr;
                data.createdAtLocationStr = createdAtLocationStr;
                data.differentLocation = differentLocation;

                records.push({
                    meta: {
                        measurer: {
                            userId: record.meta.measurer.userId,
                            lastName: record.meta.measurer.lastName,
                            firstName: record.meta.measurer.firstName,
                        },
                        user: {
                            userId: record.meta.user.userId,
                            lastName: record.meta.user.lastName,
                            firstName: record.meta.user.firstName,
                            code: record.meta.user.code,
                            displayName: record.meta.user.code ?? record.meta.user.lastName + ' ' + record.meta.user.firstName,
                        },
                        location: {
                            sectionId: record.meta.location.sectionId,
                            sectionName: record.meta.location.sectionName,
                        },
                    },
                    data: data,
                    commonInfo: {
                        lastModifiedAt: lastModifiedAt,
                        lastModifiedAtDayjs: lastModifiedAtDayjs,
                        lastModifiedAtStr: lastModifiedAtStr,
                        lastModifiedBy: record.commonInfo.lastModifiedBy,
                    },
                    id: record.id,
                } as RecordData);
            }

            if (generateMonitoringcalendarData) {

                if (record.data.recordType === 'MONITORING' ||
                    record.data.recordType === 'MEAL_MONITORING' ||
                    record.data.recordType === 'MEAL_MONITORING_WITH_VITALS') {

                    var createdAtDayStr = (createdAtDayjs.isValid()) ? createdAtDayjs.format('YYYY/MM/DD') : 'ERROR';
                    var createdAtTimeStr = (createdAtDayjs.isValid()) ? createdAtDayjs.format('HH:mm:ss') : 'ERROR';


                    var dataSource = new MatTableDataSource<MonitoringVitals>([
                        //  { index: "体温 °C", before: "36.5", while: "", after: "36.5" },
                        //  { index: "心拍", before: "80", while: "", after: "85" },
                        //  { index: "SpO2 %", before: "98", while: "", after: "97" },
                        { index: TYPES_I18N.monitoring.time, before: '', while: totalTime, after: '' },
                        { index: TYPES_I18N.monitoring.swallowCount, before: '', while: numOfSwallowings, after: '' },
                        // { index: "ムセ", before: "", while: numOfCoughs, after: "" },
                        { index: TYPES_I18N.monitoring.coughCount, before: '', while: numOfCoughs, after: '' },
                        // { index: "食事", before:"", while:"100", after:"" },
                    ])

                    var monitoringMoment = moment(createdAtDayStr, 'YYYY/MM/DD', true); // strict parse for calendar purposes

                    if(this.sameDay(previousMonitoringMoment, monitoringMoment)){
                        allMonitoringDayData[allMonitoringDayData.length - 1].monitoringDatas.push({ time: createdAtTimeStr, dataSource: dataSource });
                    } else {
                        allMonitoringDayData.push({
                            moment: monitoringMoment, dayStr: createdAtDayStr, monitoringDatas:
                                [{ time: createdAtTimeStr, dataSource: dataSource }]
                        });

                        previousMonitoringMoment = monitoringMoment;
                    }

                    // if (this.sameDay(previousMonitoringDay, monitoringDate)) {
                    //     allMonitoringDayData[allMonitoringDayData.length - 1].monitoringDatas.push({ time: createdAtTimeStr, dataSource: dataSource });
                    // } else {
                    //     allMonitoringDayData.push({
                    //         day: monitoringDate, dayStr: createdAtDayStr, monitoringDatas:
                    //             [{ time: createdAtTimeStr, dataSource: dataSource }]
                    //     });

                    //     previousMonitoringDay = monitoringDate;
                    // }
                }
            }
        }

        return {records: records, monitoringDaysData: allMonitoringDayData};
    }

    private static sameDay(d1: moment.Moment, d2: moment.Moment): boolean {
        if (d1 == null || d2 == null) {
            return false;
        }
        return d1.isSame(d2);
    }
}

export interface MonitoringDayData {
    dayStr: string;
    moment: moment.Moment;
    monitoringDatas: MonitoringData[];
}

export interface MonitoringData {
    time: string;
    dataSource: MatTableDataSource<MonitoringVitals>;
}

export interface MonitoringVitals {
    index: string;
    before: string;
    while: string;
    after: string;
}

export interface QuestionnaireAnswer {
    questionCode: string;
    score: number;
}

export interface QuestionnaireAnswer {
    waterPuddingSwallowPart: WaterPuddingSwallowPart;
    drySwallowPart: DrySwallowPart;
    interimScore: number;
}

export interface AttemptDetail {
    waterPuddingSwallowPart: WaterPuddingSwallowPart;
    drySwallowPart: DrySwallowPart;
    interimScore: number;
}

export interface WaterPuddingSwallowPart {
    timeTaken: number;
    swallowCount: number;
    swallowAvgDuration: number;
    coughCount: number;
    swallowResult: string;
    dyspneaResult: string;
    coughOrOtherResult: string;
    foodRetentionResult: string;
}

export interface DrySwallowPart {
    timeTaken: number;
    swallowCount: number;
    swallowAvgDuration: number;
    coughCount: number;
    firstDrySwallowResult: string;
    secondDrySwallowResult: string;
}

export type RecordTypeLocale = 'ja' | 'en' | 'zh-TW';

export interface RecordTypeLabels {
    ja: string;
    en: string;
    'zh-TW'?: string;
}

export interface RecordTypeDefinition {
    code: string;
    dispValue: string;
    labels: RecordTypeLabels;
}

const recordTypeLabels: Record<string, RecordTypeLabels> = {
    RSST: { ja: 'RSST', en: 'RSST', 'zh-TW': 'RSST' },
    MWST: { ja: '改訂水飲みテスト', en: 'MWST', 'zh-TW': '改良式飲水測試' },
    MWST_G: { ja: '改訂水飲みテスト', en: 'MWST', 'zh-TW': '改良式飲水測試' },
    MWST_G_DATA: { ja: '改訂水飲みテスト', en: 'MWST', 'zh-TW': '改良式飲水測試' },
    FOOD_TEST: { ja: 'フードテスト', en: 'Food test', 'zh-TW': '食物測試' },
    FOOD_TEST_G: { ja: 'フードテスト', en: 'Food test', 'zh-TW': '食物測試' },
    FOOD_TEST_G_DATA: { ja: 'フードテスト', en: 'Food test', 'zh-TW': '食物測試' },
    MONITORING: { ja: 'モニタリング', en: 'Monitoring', 'zh-TW': '監測' },
    MEAL_MONITORING: { ja: '食事モニタリング', en: 'Meal monitoring', 'zh-TW': '進食監測' },
    MEAL_MONITORING_WITH_VITALS: {
        ja: '食事モニタリング（バイタル）',
        en: 'Meal monitoring (with vitals)',
        'zh-TW': '進食監測（含生命徵象）',
    },
    VITAL: { ja: 'バイタル', en: 'Vitals', 'zh-TW': '生命徵象' },
    GOST: { ja: 'GOST', en: 'GOST', 'zh-TW': 'GOST' },
    GOST_DATA: { ja: 'GOST', en: 'GOST', 'zh-TW': 'GOST' },
    GOST_DATA_2: { ja: 'GOST', en: 'GOST', 'zh-TW': 'GOST' },
    VS: { ja: 'VS（ビデオ聴診）', en: 'VS', 'zh-TW': 'VS（影片聽診）' },
    VS_DATA: { ja: 'VS', en: 'VS', 'zh-TW': 'VS' },
    TOMASS: { ja: 'TOMASS', en: 'TOMASS', 'zh-TW': 'TOMASS' },
    TOMASS_DATA: { ja: 'TOMASS', en: 'TOMASS', 'zh-TW': 'TOMASS' },
    SSTP: { ja: '簡易飲水テスト', en: 'SSTP', 'zh-TW': '簡易飲水測試' },
    SSTP_DATA: { ja: '簡易飲水テスト', en: 'SSTP', 'zh-TW': '簡易飲水測試' },
};

export function resolveRecordTypeLocale(): RecordTypeLocale {
    try {
        // Prefer Angular build-time locale ($localize.locale) over browser language
        const angularLocale = (typeof $localize !== 'undefined')
            ? ($localize as unknown as { locale?: string }).locale
            : undefined;
        const lang = angularLocale
            || (typeof navigator !== 'undefined' && navigator.language)
            || 'ja';
        const lower = lang.toLowerCase();
        if (lower.startsWith('en')) return 'en';
        if (lower.startsWith('zh')) return 'zh-TW';
        return 'ja';
    } catch {
        return 'ja';
    }
}

export function getRecordTypeDisplay(
    recordType: string,
    locale: RecordTypeLocale = resolveRecordTypeLocale(),
): string {
    if (!recordType) {
        return '';
    }
    const labels = recordTypeLabels[recordType];
    if (!labels) {
        return recordType;
    }
    return labels[locale] ?? labels.ja ?? labels.en ?? recordType;
}

export const RecordTypes: RecordTypeDefinition[] = Object.entries(recordTypeLabels).map(
    ([code, labels]) => ({
        code,
        dispValue: labels.ja,
        labels,
    }),
);

export const FileTypes = [  // { display: TYPES_I18N.fileTypes.data, value: 'DATA' }, // CSVファイルはユーザが特に選ばなくても自動的に含まれることにする
    { display: TYPES_I18N.fileTypes.audio, value: 'AUDIO' },
    { display: TYPES_I18N.fileTypes.image, value: 'IMAGE' },
    { display: TYPES_I18N.fileTypes.movie, value: 'MOVIE'},
    { display: TYPES_I18N.fileTypes.detail, value: 'DETAIL'},
];

// export interface RecordData {
//     createdAt: string;
//     userName: string;
//     name: string;
//     recordType: string;
//     recordTypeView: string;
//     numOfCough: number;
//     totalMinutes: number;
//     totalTime: string;
//     numOfSwallowing: number;
//     meanSwallowingTime: number;
//     sd: number;
//     progress: string;
//     evaluation: number;
//     id: string;
// }
